import { FilterOperator, FilterCondition } from './../../models/models.swagger.generated';
import { IPagedItems } from './paged-items.interface';
import { IAdaptorService } from './adaptor-service.interface';
import { fakeHttpRequest } from './../fakeHttpRequest';
import { CustomDataAdaptor, Predicate, CrudOptions, DataResult, DataOptions, Query } from '@syncfusion/ej2-data';
import { StringHelper } from '../string-helper';
import { FilterDTO, SortDirection } from '../../models/models.swagger.generated';
import { SpinnerService } from '../../spinner/spinner.service';

interface SearchParams {
  fields: string[];
  operator: string;
  key: string;
}

interface CrudCallbackArgs {
  data: string;
  onSuccess: (data: unknown, options?: { httpRequest: fakeHttpRequest }) => void;
  onFailure: (error: unknown) => void;
}

export class AvpCustomAdaptor extends CustomDataAdaptor {
  private _lastQueryInfo: {
    filters?: FilterDTO[] | null;
    skip?: number;
    take?: number;
    sortBy?: string;
    sortDirection?: SortDirection;
  } = {};

  constructor(private service: IAdaptorService, private spinnerService: SpinnerService, private ignoreSearch: boolean = false) {
    super();
    this.options.updateRecord = this.updateRecord.bind(this);
    this.options.addRecord = this.addRecord.bind(this);
    this.options.getData = this.getData.bind(this);
    this.options.deleteRecord = this.deleteRecord.bind(this);
  }

  public getLastQueryInfo() {
    return this._lastQueryInfo;
  }

  public override processResponse(data: DataResult, ds?: DataOptions, query?: Query, xhr?: Request, request?: Object, changes?: CrudOptions): DataResult {
    /* if (request == null) {
      console.log(xhr, request, changes, data, ds, query);
    } */
    return super.processResponse(data, ds, query, xhr, request ?? data, changes);
  }

  private updateRecord(args: CrudCallbackArgs) {
    const data = JSON.parse(args.data);
    this.spinnerService.show();
    this.service.updateRecord(data?.value).subscribe({ next: (response) => {
      this.spinnerService.hide();
      args.onSuccess(response, {httpRequest: new fakeHttpRequest()});
    }, error: (error) => {
      this.spinnerService.hide();
      args.onFailure('updateRecord failed');
    }});
  }

  private addRecord(args: CrudCallbackArgs) {
    const data = JSON.parse(args.data);
    this.spinnerService.show();
    this.service.addRecord(data?.value).subscribe({ next: (response) => {
      this.spinnerService.hide();
      args.onSuccess(response, {httpRequest: new fakeHttpRequest()});
    }, error: (error) => {
      this.spinnerService.hide();
      args.onFailure('addRecord failed');
    }});
  }

  private deleteRecord(args: CrudCallbackArgs) {
    const data = JSON.parse(args.data);
    this.service.deleteRecord(data?.key).subscribe({ next: (response) => {
      args.onSuccess({}, {httpRequest: new fakeHttpRequest()});
    }, error: (error) => {
      args.onFailure('delete failed');
    }});
  }

  private getData(args: CrudCallbackArgs) {
    const data = JSON.parse(args.data);
    let filters: FilterDTO[] | null = [];
    if (data.where && data.where.length > 0) {
      filters = this.handleWhereData(data.where as Predicate[]);
    }
    if (data.search && data.search.length > 0 && !this.ignoreSearch) {
      const filter = this.handleSearchData(data.search[0] as SearchParams);
      if (filter) {
        filters.push(filter);
      }
    }
    if (filters.length === 0) {
      filters = null;
    }
    let sortBy: string | undefined;
    let sortDirection: SortDirection | undefined;
    if (data.sorted) {
      sortBy = StringHelper.capitalizeFirstLetter(data.sorted[0].name);
      sortDirection = data.sorted[0].direction === 'descending' ? SortDirection.Descending : SortDirection.Ascending;
    }
    this._lastQueryInfo = {
      filters,
      skip: data?.skip,
      take: data?.take,
      sortBy,
      sortDirection
    };
    this.service.getData(data?.skip, data?.take, filters, sortBy, sortDirection).subscribe({ next: (pagedItems: IPagedItems | null) => {
      let res: any;
      if (data.requiresCounts) {
        res = {result: pagedItems?.items, count: pagedItems ? pagedItems.totalNumberOfItems : 0};
      }
      else {
        res = pagedItems?.items;
      }
      args.onSuccess(res, {httpRequest: new fakeHttpRequest()});
    }, error: (error) => {
      args.onFailure('getData failed');
    }});
  }

  private handleWhereData(whereData: Predicate[]): FilterDTO[] {
    const filters: FilterDTO[] = [];
    whereData.forEach(predicate => {
      this.addNonComplexFilter(predicate, filters);
      this.addComplexFilter(predicate, filters);
    });
    return filters;
  }

  private handleSearchData(searchData: SearchParams): FilterDTO | null {
    if (!searchData.fields || !searchData.fields.length) {
      return null;
    }
    const operator = this.getFilterOperator(searchData.operator);
    if (operator === null) {
      return null;
    }
    const filter = new FilterDTO({isComplex: true, condition: FilterCondition.Or, predicates: []});
    searchData.fields.forEach(f => {
      filter.predicates?.push(new FilterDTO({
        isComplex: false,
        field: StringHelper.capitalizeFirstLettersOfPropertyChain(f),
        operator,
        value: searchData.key
      }));
    });
    return filter;
  }

  private addComplexFilter(filterData: Predicate, filterPredicates: FilterDTO[]): void {
    if (!filterData.isComplex || filterData.predicates == null || filterData.predicates.length === 0) {
      return;
    }

    const condition = this.getCondition(filterData.condition);
    if (condition === null) {
      return;
    }
    const filter = new FilterDTO({
      isComplex: true,
      condition,
      predicates: []
    });
    filterData.predicates.forEach(predicate => {
      this.addNonComplexFilter(predicate, filter.predicates!);
      this.addComplexFilter(predicate, filter.predicates!);
    });
    if (filter.predicates!.length > 0) {
      filterPredicates.push(filter);
    }
  }

  private addNonComplexFilter(filterData: Predicate, filterPredicates: FilterDTO[]): void {
    if (filterData.isComplex) {
      return;
    }
    const operator = this.getFilterOperator(filterData.operator);
    if (operator === null) {
      return;
    }
    if (filterData.value == null && operator !== FilterOperator.Equal && operator !== FilterOperator.NotEqual) {
      // this filter would fail anyway
      return;
    }
    filterPredicates?.push( new FilterDTO({
      isComplex: false,
      field: StringHelper.capitalizeFirstLettersOfPropertyChain(filterData.field),
      operator,
      value: filterData.value?.toString()
    }));
  }

  private getFilterOperator(operator: string): FilterOperator | null {
    if (operator === 'startswith') {
      return FilterOperator.StartsWith;
    }
    if (operator === 'endswith') {
      return FilterOperator.EndsWith;
    }
    if (operator === 'contains') {
      return FilterOperator.Contains;
    }
    if (operator === 'equal') {
      return FilterOperator.Equal;
    }
    if (operator === 'notequal') {
      return FilterOperator.NotEqual;
    }
    if (operator === 'greaterthan') {
      return FilterOperator.GreaterThan;
    }
    if (operator === 'greaterthanorequal') {
      return FilterOperator.GreaterThanOrEqual;
    }
    if (operator === 'lessthan') {
      return FilterOperator.LessThan;
    }
    if (operator === 'lessthanorequal') {
      return FilterOperator.LessThanOrEqual;
    }
    if (operator === 'isempty') {
      return FilterOperator.Empty;
    }
    if (operator === 'isnotempty') {
      return FilterOperator.NotEmpty;
    }
    if (operator === 'doesnotstartwith') {
      return FilterOperator.DoesNotStartWith;
    }
    if (operator === 'doesnotendwith') {
      return FilterOperator.DoesNotEndWith;
    }
    if (operator === 'doesnotcontain') {
      return FilterOperator.DoesNotContain;
    }
    return null;
  }

  private getCondition(condition: string): FilterCondition | null {
    if (condition === 'and') {
      return FilterCondition.And;
    }
    if (condition === 'or') {
      return FilterCondition.Or;
    }
    return null;
  }
}
